import { BellOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Divider, Flex } from 'antd';
import { useTranslation } from 'react-i18next';

import Empty from '@/UI/Empty/Empty';
import HeaderIconPopover from '@/UI/HeaderIconPopover/HeaderIconPopover';
import Loader from '@/UI/Loader';
import { LabelTitle, Meta } from '@/UI/typography';
import { formatRelativeToNow } from '@/utils/formatDate';

import type { PopoverProps } from 'antd';
import type { RefObject, UIEventHandler } from 'react';

/** Inner panel owns scrolling; disable global popover root scroll. */
const NOTIFICATION_POPOVER_STYLES: PopoverProps['styles'] = {
  root: {
    maxHeight: 'none',
    overflow: 'visible',
  },
  content: {
    minWidth: 352,
    maxWidth: 360,
  },
};

export type NotificationPopupItem = {
  id: string;
  notificationType: number;
  createdAt: string;
  name: string;
  stage: string;
  message: string;
  link: string;
  disabled: boolean;
  showUnreadDot: boolean;
};

type NotificationPopupProps = {
  unreadCount: number;
  isLoading: boolean;
  /** When false, hides the mark-as-read control (e.g. admin or zero unread). */
  showMarkAsRead: boolean;
  items: NotificationPopupItem[];
  locale: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  onScroll: UIEventHandler<HTMLDivElement>;
  onMarkAsRead: () => void;
  /**
   * Item activation. `closePopover` dismisses the header popover (e.g. after
   * navigate); callers decide when to invoke it.
   */
  onItemClick: (item: NotificationPopupItem, closePopover: () => void) => void;
};

export const notificationCardTestId = (id: string) => `notification-card-${id}`;

type NotificationPanelProps = Pick<
  NotificationPopupProps,
  | 'isLoading'
  | 'showMarkAsRead'
  | 'items'
  | 'locale'
  | 'scrollRef'
  | 'onScroll'
  | 'onMarkAsRead'
  | 'onItemClick'
> & {
  closePopover: () => void;
};

const NotificationPanel = ({
  isLoading,
  showMarkAsRead,
  items,
  locale,
  scrollRef,
  onScroll,
  onMarkAsRead,
  onItemClick,
  closePopover,
}: NotificationPanelProps) => {
  const [t] = useTranslation();

  return (
    <Flex vertical>
      <Flex justify="space-between" align="center" gap="small">
        <LabelTitle>{t('NOTIFICATION_TITLE')}</LabelTitle>
        {showMarkAsRead && (
          <Button
            size="small"
            data-testid="notifications-mark-as-read"
            onClick={onMarkAsRead}
          >
            {t('MARK_AS_READ')}
          </Button>
        )}
      </Flex>
      <Divider style={{ margin: 0 }} />
      <Loader spinning={isLoading}>
        <div
          ref={scrollRef}
          data-testid="notifications-list-scroll"
          style={{ maxHeight: '50vh', overflowY: 'auto' }}
          onScroll={onScroll}
        >
          <Flex vertical gap="small">
            {!isLoading && !items.length ? (
              <Empty size="small" description={t('NO_NOTIFICATIONS')} />
            ) : (
              items.map((item) => (
                <Card
                  key={item.id}
                  data-testid={notificationCardTestId(item.id)}
                  data-notification-type={item.notificationType}
                  data-disabled={item.disabled ? 'true' : 'false'}
                  variant="borderless"
                  size="small"
                  hoverable={!!item.link && !item.disabled}
                  style={{
                    opacity: item.disabled ? 0.5 : 1,
                    cursor: item.disabled
                      ? 'not-allowed'
                      : item.link
                        ? 'pointer'
                        : 'default',
                  }}
                  onClick={() => onItemClick(item, closePopover)}
                >
                  <Flex justify="space-between">
                    <Flex vertical gap={0}>
                      <LabelTitle>
                        {item.name || t('NEW_NOTIFICATION')}
                      </LabelTitle>
                      <Meta>
                        {[item.stage, item.message].filter(Boolean).join(' — ')}
                      </Meta>
                    </Flex>
                    <Flex gap={4} align="baseline" style={{ flexShrink: 0 }}>
                      {item.showUnreadDot && <Badge color="primary" />}
                      <Meta>{formatRelativeToNow(item.createdAt, locale)}</Meta>
                    </Flex>
                  </Flex>
                </Card>
              ))
            )}
            <div data-testid="notifications-list-loader" />
          </Flex>
        </div>
      </Loader>
    </Flex>
  );
};

/**
 * Presentational notifications bell + panel. Call sites resolve data and
 * pass items/handlers — this kit does not import `@/features`.
 */
const NotificationPopup = ({
  unreadCount,
  isLoading,
  showMarkAsRead,
  items,
  locale,
  scrollRef,
  onScroll,
  onMarkAsRead,
  onItemClick,
}: NotificationPopupProps) => {
  const [t] = useTranslation();

  return (
    <HeaderIconPopover
      ariaLabel={t('NOTIFICATION_TITLE')}
      buttonTestId="notifications-bell"
      placement="rightTop"
      popoverStyles={NOTIFICATION_POPOVER_STYLES}
      icon={
        <Badge status="success" count={unreadCount}>
          <BellOutlined />
        </Badge>
      }
      content={(closePopover) => (
        <NotificationPanel
          isLoading={isLoading}
          showMarkAsRead={showMarkAsRead}
          items={items}
          locale={locale}
          scrollRef={scrollRef}
          onScroll={onScroll}
          onMarkAsRead={onMarkAsRead}
          onItemClick={onItemClick}
          closePopover={closePopover}
        />
      )}
    />
  );
};

export default NotificationPopup;
